import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import focusTrap from 'focus-trap-js';
import { createPortal } from 'react-dom';
import Image from 'components/Image';
import { transformVariation } from 'data/dutchie/transformations';
import useContent from 'hooks/useContent';
import abbreviateUnitOfMeasure from 'utils/abbreviate-uom';
import styles from './AddToBagModal.module.scss';

export default function AddToBagModal({
  category,
  customMessage,
  displayVariation,
  imageUrl,
  isError,
  mgCBD,
  mgTHC,
  percentCBD,
  percentTHC,
  productName,
  weight,
  weightUOM,
  variation,
  onClose,
}) {
  const content = useContent('add_item_card');

  useEffect(() => {
    if (content) {
      setTimeout(
        () => document.querySelector('#added_to_bag_modal')?.focus(),
        250
      );
      setTimeout(() => onClose(), 1750);
    }
  }, [content]);

  const contRef = useRef();

  useEffect(() => {
    const handleKeyEvent = (event) => {
      focusTrap(event, contRef.current);
    };
    document.addEventListener('keydown', handleKeyEvent);
    return () => {
      document.removeEventListener('keydown', handleKeyEvent);
    };
  }, [contRef]);

  if (!content) {
    return null;
  }

  const { error_message: errorMessage, title: modalTitle } = content;

  const title = customMessage || (isError ? errorMessage : modalTitle);

  const thcCbd = [
    percentTHC &&
      `THC: ${percentTHC}${
        typeof percentTHC === 'string' && percentTHC.endsWith('%') ? '' : '%'
      }`,
    mgTHC && `THC: ${mgTHC}mg`,
    percentCBD &&
      `CBD: ${percentCBD}${
        typeof percentCBD === 'string' && percentCBD.endsWith('%') ? '' : '%'
      }`,
    mgCBD && `CBD: ${mgCBD}mg`,
  ]
    .filter(Boolean)
    .join(' | ');

  return createPortal(
    <>
      <button
        className={`border-0 m-0 p-0 position-fixed ${styles.overlay}`}
        type="button"
        onClick={onClose}
      />
      <div
        ref={contRef}
        aria-label={title}
        className={`mx-auto position-fixed p-8 ${styles.modal}`}
        id="added_to_bag_modal"
        tabIndex="-1"
        role="dialog"
      >
        <div className="d-block border-0 pb-4">
          <div className="d-flex flex-row justify-content-between">
            <h2
              className={classNames(
                'font-roboto fs-7 fw-700 text-primary',
                styles.title,
                {
                  'text-danger': isError,
                }
              )}
            >
              {title}
            </h2>
          </div>
        </div>
        <div className="pt-0">
          {isError ? null : (
            <div className="d-flex flex-row">
              <Image
                alt={`${productName}`}
                height={80}
                className={styles.image}
                src={imageUrl}
                width={80}
              />
              <div className="d-flex flex-column fs-7">
                <strong className="font-sofia fw-700 text-primary">
                  {productName}
                </strong>

                {displayVariation ? (
                  <span className="fs-6 fw-700 text-primary">
                    {transformVariation(displayVariation, category)}
                  </span>
                ) : weight && weightUOM ? (
                  <span className="fs-6 fw-700 text-primary">{`${weight}${abbreviateUnitOfMeasure(
                    weightUOM
                  )}`}</span>
                ) : variation ? (
                  <span className="fs-6 fw-700 text-primary">{variation}</span>
                ) : null}
                {thcCbd ? <span className="fs-6">{thcCbd}</span> : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.querySelector('body')
  );
}
